<script lang="ts">
export default {
  name: 'UniversalSearchModal',
};
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { useBodyScrollLock } from 'reka-ui';
import UniversalSearch from '@/components/UniversalSearch.vue';

const open = defineModel<boolean>('open', { default: false });

// Share reka's lock stack rather than locking by hand: the type dropdown locks too, and its
// release would otherwise strip the scrollbar-width compensation while the palette is still
// open, shifting the page sideways. Locking here also makes the dropdown's lock a no-op.
const bodyScrollLocked = useBodyScrollLock(false);
watch(open, (value) => (bodyScrollLocked.value = value), { immediate: true });

const close = () => {
  open.value = false;
};

const hasOpenDismissableLayer = () => !!document.querySelector('[data-dismissable-layer]');

const handleKeydown = (event: KeyboardEvent) => {
  // Chrome autofill dispatches keydown events with no `key`.
  if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    open.value = !open.value;
    return;
  }

  // reka dismisses the type dropdown on Escape but does not mark the event handled, so without
  // this the same Escape would collapse the dropdown and the palette together.
  if (event.key === 'Escape' && open.value && !hasOpenDismissableLayer()) {
    event.preventDefault();
    close();
  }
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <Teleport to="body">
    <!--
      The backdrop deliberately does not dismiss. Selecting a type-filter option leaves bare
      backdrop where the listbox was (reka removes it on pointerup, so no click event is even
      dispatched for the option). A dismissing backdrop would tear the palette down under the
      next click of a rapid pair, and that click's successor then lands on the page underneath
      and selects its text. Escape and the close button are the ways out.

      pointer-events-auto is required, not decorative: the body scroll lock sets
      `pointer-events: none` on body, which the overlay would otherwise inherit. Re-enabling it
      here alone also leaves the page behind the palette inert.
    -->
    <div
      v-if="open"
      data-testid="universal-search-overlay"
      class="pointer-events-auto fixed inset-0 z-1100 flex justify-center bg-black/40 px-4 pt-[10vh]">
      <div
        data-testid="universal-search-panel"
        class="h-fit w-full max-w-3xl">
        <UniversalSearch
          autofocus
          modal
          @navigate="close"
          @dismiss="close" />
      </div>
    </div>
  </Teleport>
</template>
